import React, { useMemo, useState } from "react";

import { Check, Building2, MapPin, Lock } from "lucide-react";

import {
  Account,
  AccountTeamsBatchEntry,
  AccountTeamsByAccountId,
  StakeholderTab,
} from "./types";
import SearchInput from "../../../../Components/Search/SearchInput";
import { useAccountTiers } from "../../../../Utils/Helper/accountTierHelpers";
import { formatCompactCurrency } from "../../../../Utils/Helper/formatCompactCurrency";

const getStakeholderLists = (
  account: Account,
  teams?: AccountTeamsBatchEntry
) => {
  const assignedSponsors = teams?.assignedSponsors?.length
    ? teams.assignedSponsors
    : (account.executives ?? []);

  return {
    assignedSponsors,
    accountTeam: teams?.accountTeam ?? [],
    otherExecutives: teams?.sponsors ?? [],
  };
};

const accountHasStakeholders = (
  account: Account,
  teams?: AccountTeamsBatchEntry
) => {
  const lists = getStakeholderLists(account, teams);
  return (
    lists.assignedSponsors.length > 0 ||
    lists.accountTeam.length > 0 ||
    lists.otherExecutives.length > 0
  );
};

const formatSponsorRole = (role: string) => {
  if (!role) return "Executive Sponsor";
  return role
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const getSelectionSummary = (
  selectedIds: string[],
  lists: ReturnType<typeof getStakeholderLists>
) => {
  const countSelected = (items: { id: string }[]) =>
    items.filter(item => selectedIds.includes(item.id)).length;

  return {
    sponsorCount: countSelected(lists.assignedSponsors),
    otherExecutiveCount: countSelected(lists.otherExecutives),
    selectedAccountTeamCount: countSelected(lists.accountTeam),
    accountTeamTotal: lists.accountTeam.length,
    totalStakeholders:
      lists.assignedSponsors.length +
      lists.accountTeam.length +
      lists.otherExecutives.length,
  };
};

interface Step3Props {
  accounts: Account[];
  selectedAccountIds: string[];
  accountExecutives: Record<string, string[]>;
  accountTeamsByAccountId?: AccountTeamsByAccountId;
  teamsLoading?: boolean;
  lockedAccountIds?: string[];
  isOngoingEdit?: boolean;
  onToggleAccount: (accountId: string) => void;
  onToggleExecutive: (accountId: string, executiveId: string) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
}

const STAKEHOLDER_TABS: { id: StakeholderTab; label: string }[] = [
  { id: "sponsors", label: "Assigned Sponsors" },
  { id: "accountTeam", label: "Account Team" },
  { id: "otherExecutives", label: "Other Executives" },
];

interface StakeholderCardProps {
  name: string;
  subtitle: string;
  isSelected: boolean;
  isLocked: boolean;
  onSelect: () => void;
}

const StakeholderCard = ({
  name,
  subtitle,
  isSelected,
  isLocked,
  onSelect,
}: StakeholderCardProps) => (
  <button
    type="button"
    disabled={isLocked}
    onClick={onSelect}
    className={`flex w-full items-center gap-2 rounded-md border p-2.5 text-left transition-colors ${
      isSelected
        ? "border-purple-300 bg-purple-50"
        : "border-gray-200 bg-white hover:bg-gray-50"
    } ${isLocked ? "cursor-not-allowed opacity-80" : "cursor-pointer"}`}
  >
    <div className="min-w-0 flex-1">
      <div className="truncate text-sm font-medium text-gray-900">{name}</div>
      <div className="truncate text-xs text-gray-500">{subtitle}</div>
    </div>
    {isSelected && <Check className="h-4 w-4 shrink-0 text-purple-600" />}
  </button>
);

const EmptyTabMessage = ({ message }: { message: string }) => (
  <div className="rounded-md bg-gray-50 p-3 text-center text-sm text-gray-500">
    {message}
  </div>
);

const Step3AccountTargeting: React.FC<Step3Props> = ({
  accounts,
  selectedAccountIds,
  accountExecutives,
  accountTeamsByAccountId = {},
  teamsLoading = false,
  lockedAccountIds = [],
  isOngoingEdit = false,
  onToggleAccount,
  onToggleExecutive,
  onSelectAll,
  onClearAll,
}) => {
  const { formatTier } = useAccountTiers();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTabByAccount, setActiveTabByAccount] = useState<
    Record<string, StakeholderTab>
  >({});

  const activeTab = (accountId: string): StakeholderTab =>
    activeTabByAccount[accountId] ?? "sponsors";

  const disabledAccounts = useMemo(
    () =>
      accounts.filter(
        account =>
          !accountHasStakeholders(account, accountTeamsByAccountId[account.id])
      ),
    [accounts, accountTeamsByAccountId]
  );

  const filteredAccounts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return accounts;

    return accounts.filter(account => {
      const haystack = [
        account.name,
        account.tier,
        account.city,
        account.country,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [accounts, searchQuery]);

  const renderStakeholderTab = (
    account: Account,
    tab: StakeholderTab,
    isLockedAccount: boolean
  ) => {
    const teams = accountTeamsByAccountId[account.id];
    const lists = getStakeholderLists(account, teams);
    const selectedIds = accountExecutives[account.id] ?? [];
    const isTeamsPending = teamsLoading && !teams;

    if (tab === "sponsors") {
      if (!lists.assignedSponsors.length) {
        return (
          <EmptyTabMessage message="No assigned sponsors for this account" />
        );
      }

      return (
        <div className="space-y-2">
          {lists.assignedSponsors.map(person => (
            <StakeholderCard
              key={person.id}
              name={person.name}
              subtitle={person.role}
              isSelected={selectedIds.includes(person.id)}
              isLocked={isLockedAccount}
              onSelect={() => onToggleExecutive(account.id, person.id)}
            />
          ))}
        </div>
      );
    }

    if (tab === "accountTeam") {
      if (isTeamsPending) {
        return <EmptyTabMessage message="Loading account team..." />;
      }
      if (!lists.accountTeam.length) {
        return (
          <EmptyTabMessage message="No account team members for this account" />
        );
      }

      return (
        <div className="space-y-2">
          {lists.accountTeam.map(person => (
            <StakeholderCard
              key={person.id}
              name={person.name}
              subtitle={person.title || person.role}
              isSelected={selectedIds.includes(person.id)}
              isLocked={isLockedAccount}
              onSelect={() => onToggleExecutive(account.id, person.id)}
            />
          ))}
        </div>
      );
    }

    if (isTeamsPending) {
      return <EmptyTabMessage message="Loading other executives..." />;
    }
    if (!lists.otherExecutives.length) {
      return <EmptyTabMessage message="No other executives for this account" />;
    }

    return (
      <div className="space-y-2">
        {lists.otherExecutives.map(person => (
          <StakeholderCard
            key={person.id}
            name={person.name}
            subtitle={formatSponsorRole(person.role)}
            isSelected={selectedIds.includes(person.id)}
            isLocked={isLockedAccount}
            onSelect={() => onToggleExecutive(account.id, person.id)}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Select Target Accounts
          </h3>
          <p className="mt-1 text-sm text-gray-600">
            {isOngoingEdit
              ? "For ongoing assessments, existing accounts are locked. You can only add new accounts."
              : "Choose which accounts should be included in this assessment"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onSelectAll}
            className="h-9 rounded-md border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50"
          >
            Select All
          </button>
          <button
            onClick={onClearAll}
            disabled={isOngoingEdit}
            className="h-9 rounded-md border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Clear All
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm">
        <p className="font-medium text-gray-700">
          Selected: {selectedAccountIds.length} of {accounts.length} accounts
        </p>
        <p className="text-xs text-gray-500">
          Showing {filteredAccounts.length} of {accounts.length} accounts
        </p>
      </div>

      <SearchInput
        placeholder="Search by account name, tier, city, or country..."
        value={searchQuery}
        onChange={event => setSearchQuery(event.target.value)}
        rounded="normal"
      />

      <div className="max-h-96 space-y-3 overflow-y-auto pr-1">
        {filteredAccounts.map(account => {
          const isSelected = selectedAccountIds.includes(account.id);
          const isLocked =
            isOngoingEdit &&
            isSelected &&
            lockedAccountIds.includes(account.id);
          const teams = accountTeamsByAccountId[account.id];
          const lists = getStakeholderLists(account, teams);
          const stakeholderCount =
            lists.assignedSponsors.length +
            lists.accountTeam.length +
            lists.otherExecutives.length;
          const canSelect = stakeholderCount > 0;
          const summary = getSelectionSummary(
            accountExecutives[account.id] ?? [],
            lists
          );

          return (
            <div
              key={account.id}
              className={`rounded-lg border-2 p-4 transition-all ${
                !canSelect
                  ? "border-gray-200 bg-gray-50 opacity-60"
                  : isSelected
                    ? "border-purple-500 bg-purple-50"
                    : "border-gray-200 hover:border-purple-300 hover:shadow-sm"
              }`}
            >
              <div
                className={`mb-3 flex items-start justify-between ${
                  canSelect && !isLocked
                    ? "cursor-pointer"
                    : "cursor-not-allowed"
                }`}
                onClick={() => {
                  if (canSelect && !isLocked) onToggleAccount(account.id);
                }}
              >
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900">
                    {account.name}
                  </h4>
                  <div className="mt-1 space-y-1 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <Building2 className="h-3 w-3" />
                      <span>
                        {formatTier(account.tier) || account.tier || "-"}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      <span>
                        {account.city}, {account.country}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span>{formatCompactCurrency(account.revenue)}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 pl-3">
                  {isLocked && (
                    <span className="inline-flex items-center rounded-md border border-gray-200 bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                      <Lock className="mr-1 h-3 w-3" />
                      Already selected
                    </span>
                  )}
                  <span
                    className={`whitespace-nowrap text-sm ${
                      canSelect ? "text-gray-500" : "text-gray-400"
                    }`}
                  >
                    {stakeholderCount}{" "}
                    {stakeholderCount === 1 ? "stakeholder" : "stakeholders"}
                  </span>
                  {isSelected && canSelect && (
                    <Check className="h-5 w-5 text-purple-600" />
                  )}
                </div>
              </div>

              {isSelected && canSelect && (
                <div className="border-t border-purple-200 pt-3">
                  <h5 className="mb-2.5 text-sm font-semibold text-gray-800">
                    Account Stakeholders
                  </h5>

                  <div className="mb-3 flex border-b border-gray-200">
                    {STAKEHOLDER_TABS.map(tab => (
                      <button
                        key={tab.id}
                        type="button"
                        className={`border-b-2 px-3 py-2 text-xs font-medium transition-colors sm:text-sm ${
                          activeTab(account.id) === tab.id
                            ? "border-purple-600 text-purple-700"
                            : "border-transparent text-gray-500 hover:text-gray-700"
                        }`}
                        onClick={event => {
                          event.stopPropagation();
                          setActiveTabByAccount(prev => ({
                            ...prev,
                            [account.id]: tab.id,
                          }));
                        }}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {renderStakeholderTab(
                    account,
                    activeTab(account.id),
                    isLocked
                  )}

                  <p className="mt-2.5 text-xs text-gray-500">
                    Selected for assessment: {summary.sponsorCount} sponsor
                    {summary.sponsorCount === 1 ? "" : "s"},{" "}
                    {summary.otherExecutiveCount} additional executive
                    {summary.otherExecutiveCount === 1 ? "" : "s"},{" "}
                    {summary.selectedAccountTeamCount} of{" "}
                    {summary.accountTeamTotal} account team member
                    {summary.accountTeamTotal === 1 ? "" : "s"} (of{" "}
                    {summary.totalStakeholders} total stakeholder
                    {summary.totalStakeholders === 1 ? "" : "s"})
                  </p>
                </div>
              )}
            </div>
          );
        })}

        {filteredAccounts.length === 0 && (
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center">
            <p className="text-sm font-medium text-gray-700">
              No accounts found
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Try a different keyword for name, tier, city, or country.
            </p>
          </div>
        )}

        {disabledAccounts.length > 0 && (
          <div className="py-4 text-center text-sm text-gray-500">
            <p>Some accounts have no stakeholders and cannot be selected:</p>
            <div className="mt-2 space-y-1">
              {disabledAccounts.map(account => (
                <div key={account.id} className="text-xs">
                  {account.name} (0 stakeholders)
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Step3AccountTargeting;
